import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Flipper } from 'react-flip-toolkit';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';

import { ExperienceFailure } from '@confluence/experience-tracker/entry-points/component';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { expValEquals } from '@confluence/feature-experiments';

import type { PresenceContentMode, PresencePageContentType } from '../presenceTypes';
import { useTransformedParticipants } from '../presence-store';
import { PresenceAvatarGroup } from '../presence-avatar-group';

import { usePagePresence } from './usePagePresence';

const i18n = defineMessages({
	anonymousUser: {
		id: 'team-presence.avatar-group.anonymous-user',
		defaultMessage: 'Anonymous Person',
		description:
			'The default user name provided for anonymous users when displayed in the avatar group within the real-time presence experience',
	},
});

export type PresenceProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresencePageContentType;
	activity: PresenceActivity;
	analyticSource?: string;
};

export const PagePresence: FC<PresenceProps> = ({
	contentId,
	contentMode,
	contentType,
	activity,
	analyticSource,
}) => {
	const { formatMessage } = useIntl();

	const { sessionId, networkError, totalParticipants } = usePagePresence(
		contentId,
		contentType,
		contentMode,
		activity,
	);

	const participants = useTransformedParticipants({
		activity,
		sessionId,
		anonymousName: formatMessage(i18n.anonymousUser),
	});

	if (networkError) {
		return (
			<ExperienceFailure name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} error={networkError} />
		);
	}

	const nonGuestShowsDefaultProfile: boolean = participants.some(
		(participant) => participant.src === '' && !participant.isGuest,
	);
	const showsDefaultProfile: boolean = participants.some((participant) => participant.src === '');

	return (
		<>
			<UFOCustomData
				data={{
					nonGuestShowsDefaultProfile,
					showsDefaultProfile,
				}}
			/>
			<FlipperAnimationsWrapper flipKey={totalParticipants}>
				<PresenceAvatarGroup
					contentId={contentId}
					contentMode={contentMode}
					contentType={contentType}
					analyticSource={analyticSource}
					participants={participants}
					totalParticipants={totalParticipants}
				/>
			</FlipperAnimationsWrapper>
		</>
	);
};

const FlipperAnimationsWrapper = ({
	children,
	flipKey,
}: {
	children: React.ReactNode;
	flipKey: string | number;
}) => {
	if (expValEquals('cc-motion-survey-experiment', 'cohort', 'experiment')) {
		return (
			<Flipper
				flipKey={flipKey}
				spring={{
					stiffness: 200,
					damping: 28,
				}}
				staggerConfig={{
					default: {
						speed: 0.3,
					},
				}}
			>
				{children}
			</Flipper>
		);
	}
	return <>{children}</>;
};
